import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Tooltip, message } from 'antd';
import styled from 'styled-components';
import { RootState } from '../../../../store';
import {
  getObjectivesFullReport,
  parseObjectives,
  type Objective,
} from '../../../../services/riskAssessmentService';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
} from '@syncfusion/ej2-react-grids';

// ==================== STYLED COMPONENTS ====================

const TabContent = styled.div`
  min-height: 400px;
  margin-top: 16px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .text {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyStateWrapper = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 6px;
`;

const EmptyStateText = styled.div`
  color: #1F2937;
  margin-bottom: 16px;
  font-size: 15px;
  font-weight: 500;
`;

const GridWrapper = styled.div`
  background: #E0E7FF;
  border-radius: 6px;
  border: 1px solid #E0E7FF;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);  
  overflow: hidden;
  padding: 0 0 0px 2px;

  .e-grid {
    border: none;
  }

  .e-grid .e-headercell {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 10px;
    color: #6B7280;
    padding: 6px 8px;
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
  }

  .e-grid .e-headercell:last-child {
    border-right: none;
  }

  .e-grid .e-rowcell {
    border-color: #E5E7EB;
    border-right: 1px solid #E5E7EB;
    padding: 12px 8px;
    font-size: 12px;
    color: #1F2937;
    white-space: normal;
    word-break: break-word;
    line-height: 1.5;
    vertical-align: top;
  }

  .e-grid .e-rowcell:last-child {
    border-right: none;
  }

  .e-grid .e-emptyrow {
    display: none;
  }

  .e-grid .e-row.e-active .e-rowcell,
  .e-grid .e-row.e-selectionbackground .e-rowcell,
  .e-grid .e-row.e-active,
  .e-grid .e-row.e-selectionbackground {
    background: transparent !important;
  }
`;

const IndexCell = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  border: 1px solid #E5E7EB;
  border-radius: 14px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #6B7280;
`;

const AssertionChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  height: 28px;
  background: #FEF9C3;
  color: #854D0E;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 500;
  margin-right: 4px;
  margin-bottom: 4px;
`;

const RiskChip = styled.span<{ $risk: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  background: white;

  ${props => {
    switch (props.$risk) {
      case 'Med':
        return `border: 1px solid #FACC15; color: #FACC15;`;
      case 'Low':
        return `border: 1px solid #2DD4BF; color: #2DD4BF;`;
      case 'High':
        return `border: 1px solid #EF4444; color: #EF4444;`;
      case 'Unranked':
      default:
        return `border: 1px solid #60A5FA; color: #60A5FA;`;
    }
  }}

  &::before {
    content: '●';
    margin-right: 6px;
    font-size: 10px;
  }
`;

const ControlChip = styled.span<{ $clickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  margin-right: 4px;
  margin-bottom: 4px;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: all 0.2s;
  
  &:hover {
    ${props => props.$clickable && `
      background: #DBEAFE;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(30, 64, 175, 0.15);
    `}
  }
`;

const TooltipTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #1F2937;
  text-align: center;
  margin-bottom: 12px;
`;

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TooltipItem = styled.div<{ $variant?: 'assertion' | 'control' }>`
  padding: 8px 12px;
  background: ${props => props.$variant === 'control' ? '#EFF6FF' : '#FEF9C3'};
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${props => props.$variant === 'control' ? '#1E40AF' : '#854D0E'};
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: ${props => props.$variant === 'control' ? '#DBEAFE' : '#FDE68A'};
    transform: translateX(2px);
  }
`;

// ==================== MAIN COMPONENT ====================

const RiskAssessmentObjectivesTotal: React.FC = () => {
  const navigate = useNavigate();
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const annualReportId = selectedReport?.annualReportId;

  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const hasFetchedRef = useRef(false);
  const currentReportIdRef = useRef<string | undefined>(undefined);

  const fetchObjectives = useCallback(async () => {
    if (!annualReportId) return;

    setIsLoading(true);
    try {
      const response = await getObjectivesFullReport(annualReportId, {
        skip: 0,
        take: 1000,
        requiresCounts: true,
      });
      const parsed = parseObjectives(response.result || []);
      setObjectives(parsed);
    } catch (error: any) {
      console.error('Failed to fetch objectives full report:', error);
      message.error(error?.message || 'Failed to load objectives');
    } finally {
      setIsLoading(false);
    }
  }, [annualReportId]);

  useEffect(() => {
    if (!annualReportId) return;

    const isNewReport = currentReportIdRef.current !== annualReportId;
    if (isNewReport) {
      hasFetchedRef.current = false;
      currentReportIdRef.current = annualReportId;
    }

    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchObjectives();
  }, [annualReportId, fetchObjectives]);

  // ==================== COLUMN TEMPLATES ====================

  const indexTemplate = (props: Objective) => {
    return <IndexCell>{props.indexStr}</IndexCell>;
  };

  const objectivesTemplate = (props: Objective) => {
    return <div dangerouslySetInnerHTML={{ __html: props.objectives }} />;
  };

  const risksTemplate = (props: Objective) => {
    return <div>{props.risks}</div>;
  };

  const riskRankingTemplate = (props: Objective) => {
    return <RiskChip $risk={props.controlRiskRanking}>{props.controlRiskRanking}</RiskChip>;
  };

  const assertionsTemplate = (props: Objective) => {
    if (!props.financialStatementAssertions || props.financialStatementAssertions.length === 0) {
      return <span style={{ color: '#9CA3AF' }}>-</span>;
    }

    const tooltipContent = props.financialStatementAssertions.length > 1 ? (
      <div>
        <TooltipTitle>Assertions</TooltipTitle>
        <TooltipList>
          {props.financialStatementAssertions.map((assertion, index) => (
            <TooltipItem key={index}>{assertion}</TooltipItem>
          ))}
        </TooltipList>
      </div>
    ) : null;

    const assertionsContent = (
      <div>
        {props.financialStatementAssertions.map((assertion, index) => (
          <AssertionChip key={index}>{assertion}</AssertionChip>
        ))}
      </div>
    );

    if (tooltipContent) {
      return (
        <Tooltip
          title={tooltipContent}
          placement="top"
          overlayStyle={{ maxWidth: '400px' }}
          overlayInnerStyle={{
            backgroundColor: '#FFFFFF',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          }}
          color="#FFFFFF"
        >
          {assertionsContent}
        </Tooltip>
      );
    }

    return assertionsContent;
  };

  const handleNavigateToControl = (controlId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/control-activities/${controlId}`);
  };

  const controlsTemplate = (props: Objective) => {
    if (props.riskMitigatingControls && props.riskMitigatingControls.length > 0) {
      const controlsContent = (
        <div>
          {props.riskMitigatingControls.map((control, index) => {
            const controlId = props.controlActivityIds?.[index];
            return (
              <ControlChip
                key={index}
                $clickable={!!controlId}
                onClick={(e) => controlId && handleNavigateToControl(controlId, e)}
                title={controlId ? 'Click to view control details' : undefined}
              >
                {control}
              </ControlChip>
            );
          })}
        </div>
      );

      if (props.riskMitigatingControls.length > 1) {
        const tooltipContent = (
          <div>
            <TooltipTitle>Risk Mitigating Controls</TooltipTitle>
            <TooltipList>
              {props.riskMitigatingControls.map((control, index) => {
                const controlId = props.controlActivityIds?.[index];
                return (
                  <TooltipItem
                    key={index}
                    $variant="control"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (controlId) handleNavigateToControl(controlId, e);
                    }}
                    style={{ cursor: controlId ? 'pointer' : 'default' }}
                  >
                    {control}
                  </TooltipItem>
                );
              })}
            </TooltipList>
          </div>
        );

        return (
          <Tooltip
            title={tooltipContent}
            placement="top"
            overlayStyle={{ maxWidth: '400px' }}
            overlayInnerStyle={{
              backgroundColor: '#FFFFFF',
              padding: '16px',
              borderRadius: '12px',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
            }}
            color="#FFFFFF"
          >
            {controlsContent}
          </Tooltip>
        );
      }

      return controlsContent;
    }

    if (props.controlText && props.controlText.trim() !== '') {
      return <div style={{ color: '#1F2937', whiteSpace: 'pre-wrap' }}>{props.controlText}</div>;
    }

    return <span style={{ color: '#9CA3AF' }}>-</span>;
  };

  // ==================== RENDER ====================

  if (isLoading) {
    return (
      <TabContent>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}>
          <LoadingSpinner>
            <div className="spinner" />
          </LoadingSpinner>
        </div>
      </TabContent>
    );
  }

  if (objectives.length === 0) {
    return (
      <TabContent>
        <EmptyStateWrapper>
          <EmptyStateText>No objectives found across all processes.</EmptyStateText>
        </EmptyStateWrapper>
      </TabContent>
    );
  }

  return (
    <TabContent>
      <GridWrapper>
        <GridComponent
          key={`objectives-total-grid-${objectives.length}`}
          dataSource={objectives}
          allowPaging={false}
          enableHover={true}
        >
          <ColumnsDirective>
            <ColumnDirective
              field='index'
              headerText='#'
              width='100'
              textAlign='Center'
              template={indexTemplate}
            />
            <ColumnDirective
              field='objectives'
              headerText='Objective'
              textAlign='Left'
              template={objectivesTemplate}
            />
            <ColumnDirective
              field='risks'
              headerText='Risk'
              textAlign='Left'
              template={risksTemplate}
            />
            <ColumnDirective
              field='riskMitigatingControls'
              headerText='Risk Mitigating Controls'
              width='200'
              textAlign='Left'
              template={controlsTemplate}
            />
            <ColumnDirective
              field='controlRiskRanking'
              headerText='Objective Risk Ranking'
              width='180'
              textAlign='Left'
              template={riskRankingTemplate}
            />
            <ColumnDirective
              field='financialStatementAssertions'
              headerText='Assertions'
              width='200'
              textAlign='Left'
              template={assertionsTemplate}
            />
          </ColumnsDirective>
          <Inject services={[Page]} />
        </GridComponent>
      </GridWrapper>
    </TabContent>
  );
};

export default RiskAssessmentObjectivesTotal;
